"use client";

import classNames from "classnames";
import { gql } from "graphql-request";

import { ReactNode, useMemo } from "react";
import FilmWithProvider from "src/components/filmlistwithproviders/FilmWithProvider";
import { useFilminfo } from "src/lib/client/useFilminfo";
import { StreamingContent, type StreamingProviderContent } from "src/lib/movieinfo/movieinfotypes";
import { type IFilmomtale } from "src/lib/types/Filmomtale";
import styles from "./FilmListWithProviders.module.css";


//#region [Props]
type FilmListWithProvidersProps = {
    className?: string;
    films?: IFilmomtale[] | null;
    children?: ReactNode;
    hideKinoklubb?: boolean;
    noFilmsMessage?: string;
};
//#endregion

const QUERY = gql`
query($edis: [String]) {
    streamingQuery {
        getStreamingContentList( movieIds: $edis) {
            __typename
            filmwebMovieId
            id
            title
            providerContents {
                __typename
                id
                provider {
                    id
                    logo
                    name
                }
                title
                url
            }
        }
    }
}`;

//#region [Component]
export default function FilmListWithProviders({ className, films, children, hideKinoklubb, noFilmsMessage }: FilmListWithProvidersProps) {
    const edis = useMemo(() => {
        return films?.map(f => f.mainVersionEDI);
    }, [films]);
    const { fiLoading, fiData } = useFilminfo(QUERY, { edis: edis }, { active: (edis?.length ?? 0) > 0 })

    const providerMap = useMemo(() => {
        if (fiLoading || !fiData) {
            return null;
        }

        const result = new Map<string, StreamingContent>();
        fiData!.streamingQuery?.getStreamingContentList?.filter(content => (content?.providerContents?.length ?? 0) > 0)?.forEach(content => result.set(content!.filmwebMovieId!, content!));
        return result;
    }, [fiLoading, fiData]);

    if ((films?.length ?? 0) === 0 && !noFilmsMessage) {
        return null;
    }

    return <div className={classNames(styles.FilmListWithProviders, className)}>
        {(films?.length ?? 0) === 0 && <>{noFilmsMessage}</>}
        {films?.map((film, idx) => {
            const content = providerMap?.get(film.mainVersionEDI);
            return <FilmWithProvider key={`${idx}_${film._id}`} film={film} isLoadingProviders={providerMap === null} streamingId={content?.id} providers={content?.providerContents as StreamingProviderContent[]} hideKinoklubb={hideKinoklubb}>{children}</FilmWithProvider>;
        })}
    </div>;
}
//#endregion
